import { Button } from "@astryxdesign/core/Button";
import { ButtonGroup } from "@astryxdesign/core/ButtonGroup";
import { Card } from "@astryxdesign/core/Card";
import { CodeBlock } from "@astryxdesign/core/CodeBlock";
import { MetadataList, MetadataListItem } from "@astryxdesign/core/MetadataList";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TextAreaMaybeError } from "@karrtopelka/tools-kit/TextAreaMaybeError";
import { useToast } from "@astryxdesign/core/Toast";
import { ToolShell } from "@karrtopelka/tools-kit/ToolShell";
import { useMemo, useState } from "react";

/** Where a parse failed, translated from the browser's message into line/column. */
interface ParseFailure {
  message: string;
  line: number | null;
  column: number | null;
}

type Parsed =
  | { ok: true; value: unknown }
  | { ok: false; error: ParseFailure }
  | { ok: null };

/**
 * V8 reports `position N` (and newer versions `line L column C`) inside the message.
 * Recovering the line ourselves means the error can point at the offending row even on
 * engines that only give a character offset.
 */
function locate(text: string, message: string): ParseFailure {
  const lineCol = /line (\d+) column (\d+)/.exec(message);
  if (lineCol) {
    return { message, line: Number(lineCol[1]), column: Number(lineCol[2]) };
  }
  const posMatch = /position (\d+)/.exec(message);
  if (posMatch) {
    const pos = Number(posMatch[1]);
    const upTo = text.slice(0, pos);
    const line = upTo.split("\n").length;
    const column = pos - upTo.lastIndexOf("\n");
    return { message, line, column };
  }
  return { message, line: null, column: null };
}

/** Counts every node so the summary can say more than "it parsed". */
function measure(value: unknown): { keys: number; arrays: number; depth: number } {
  let keys = 0;
  let arrays = 0;
  let depth = 0;

  const walk = (node: unknown, level: number): void => {
    depth = Math.max(depth, level);
    if (Array.isArray(node)) {
      arrays += 1;
      for (const item of node) walk(item, level + 1);
      return;
    }
    if (node !== null && typeof node === "object") {
      for (const [, v] of Object.entries(node)) {
        keys += 1;
        walk(v, level + 1);
      }
    }
  };

  walk(value, 0);
  return { keys, arrays, depth };
}

const SAMPLE = `{"name":"karrtopelka","tools":["ip","json","regex"],"live":true,"since":2026}`;

export function App() {
  const [raw, setRaw] = useState("");
  const [indent, setIndent] = useState<2 | 4 | 0>(2);
  const toast = useToast();

  const parsed = useMemo<Parsed>(() => {
    const text = raw.trim();
    if (text === "") return { ok: null };
    try {
      return { ok: true, value: JSON.parse(text) as unknown };
    } catch (e) {
      return { ok: false, error: locate(text, e instanceof Error ? e.message : String(e)) };
    }
  }, [raw]);

  const output = useMemo(() => {
    if (parsed.ok !== true) return "";
    return indent === 0
      ? JSON.stringify(parsed.value)
      : JSON.stringify(parsed.value, null, indent);
  }, [parsed, indent]);

  const stats = useMemo(
    () => (parsed.ok === true ? measure(parsed.value) : null),
    [parsed],
  );

  const copy = () => {
    void navigator.clipboard
      .writeText(output)
      .then(() => toast({ body: "JSON copied", uniqueID: "copy" }))
      .catch(() => toast({ body: "Copy failed", type: "error", uniqueID: "copy" }));
  };

  const inputError =
    parsed.ok === false
      ? parsed.error.line !== null
        ? `Line ${parsed.error.line}, column ${parsed.error.column}: ${parsed.error.message}`
        : parsed.error.message
      : null;

  return (
    <ToolShell active="json" tagline="Format, validate and inspect JSON">
      <VStack gap={2}>
        <TextAreaMaybeError
          label="JSON input"
          placeholder='Paste JSON here — {"hello":"world"}'
          value={raw}
          onChange={setRaw}
          rows={8}
          error={inputError}
        />
        <HStack gap={1} wrap="wrap">
          <Button
            variant="secondary"
            size="sm"
            label="Paste sample"
            onClick={() => setRaw(SAMPLE)}
          />
          <Button
            variant="ghost"
            size="sm"
            label="Clear"
            isDisabled={raw === ""}
            onClick={() => setRaw("")}
          />
        </HStack>
      </VStack>

      {parsed.ok === true ? (
        <>
          <VStack gap={1}>
            <HStack gap={2} align="center" justify="between" wrap="wrap">
              <Text type="label" color="secondary">
                Output
              </Text>
              <HStack gap={1} align="center">
                <ButtonGroup size="sm" label="Indentation">
                  <Button
                    label="2 spaces"
                    variant={indent === 2 ? "primary" : "secondary"}
                    onClick={() => setIndent(2)}
                  />
                  <Button
                    label="4 spaces"
                    variant={indent === 4 ? "primary" : "secondary"}
                    onClick={() => setIndent(4)}
                  />
                  <Button
                    label="Minified"
                    variant={indent === 0 ? "primary" : "secondary"}
                    onClick={() => setIndent(0)}
                  />
                </ButtonGroup>
                <Button variant="secondary" size="sm" label="Copy" onClick={copy} />
              </HStack>
            </HStack>
            <CodeBlock
              language="json"
              code={output}
              width="100%"
              maxHeight={420}
              hasLineNumbers={indent !== 0}
              isWrapped={indent === 0}
            />
          </VStack>

          {stats ? (
            <VStack gap={1}>
              <Text type="label" color="secondary">
                Shape
              </Text>
              <Card padding={3}>
                <MetadataList columns="multi">
                  <MetadataListItem label="Keys">{stats.keys}</MetadataListItem>
                  <MetadataListItem label="Arrays">{stats.arrays}</MetadataListItem>
                  <MetadataListItem label="Max depth">{stats.depth}</MetadataListItem>
                  <MetadataListItem label="Minified size">
                    {new Blob([JSON.stringify(parsed.value)]).size} bytes
                  </MetadataListItem>
                </MetadataList>
              </Card>
            </VStack>
          ) : null}
        </>
      ) : null}

      <Text type="supporting" size="xsm" color="secondary">
        Everything runs in your browser. Nothing you paste is uploaded, stored or logged.
      </Text>
    </ToolShell>
  );
}
